import { PropsWithChildren } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useIsAuthenticated } from '@azure/msal-react';
import { azure, loginRequest } from 'config';

const azureAuth = new PublicClientApplication(azure);
azureAuth.initialize().then(() => {
  if (!azureAuth.getActiveAccount() && azureAuth.getAllAccounts().length > 0) {
    azureAuth.setActiveAccount(azureAuth.getAllAccounts()[0]);
  }
});

const { pathname, search } = window.location;
const redirectStartPage = `${pathname}${search}`;
const redirectLogout = { postLogoutRedirectUri: '/' };

const AuthProvider = ({ children }: PropsWithChildren) => (
  <MsalProvider instance={azureAuth}>{children}</MsalProvider>
);

const getAccessToken = async () => {
  const account = azureAuth.getAllAccounts()[0];
  try {
    const { idToken } = await azureAuth.acquireTokenSilent({ ...loginRequest, account });
    return idToken;
  } catch (error) {
    try {
      const loginHint = account.idTokenClaims?.upn;
      const { idToken } = await azureAuth.ssoSilent({ ...loginRequest, account, loginHint });
      return idToken;
    } catch (err) {
      return azureAuth.logoutRedirect({ ...redirectLogout, account });
    }
  }
};

const useAuth = () => {
  const isAuthenticated = useIsAuthenticated();

  const signIn = () => {
    const customerClickedLogout = Boolean(localStorage.getItem('clickedLogout'));
    const request = customerClickedLogout
      ? { ...loginRequest, redirectStartPage, prompt: 'login' }
      : { ...loginRequest, redirectStartPage };
    localStorage.removeItem('clickedLogout');
    return azureAuth.loginRedirect(request);
  };

  const signOut = () => {
    const account = azureAuth.getAllAccounts()[0];
    const logoutHint = account.idTokenClaims?.upn;
    return azureAuth.logoutRedirect({ ...redirectLogout, account, logoutHint });
  };

  return {
    signIn,
    signOut,
    isAuthenticated,
  };
};

export { AuthProvider, useAuth, getAccessToken };
